import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import pedirSenhaAdmin from '../common/pedirSenhaAdmin';
import { lerPesoBalanca } from '../../utils/balanca';
import { cacheMesas, getMesasCache, cacheComanda, getComandaCache } from '../../services/offlineDb';
import { fecharComEsc } from '../../utils/impressaoVenda';
import { larguraEfetivaCSS } from '../../utils/impressaoMargem';
import { tentarImprimirDiretoHtml } from '../../utils/impressaoDireta';
import { useAuth } from '../../contexts/AuthContext';
import s from './ModalMesasComandas.module.css';

const MSG_OFFLINE = 'Essa ação exige conexão com a internet.';

const fmtBRL = v =>
  Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtHora = d => d ? new Date(d).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';

// Paleta pedida pela usuária — cada status tem uma cor própria, nenhuma
// repetida, pra identificação instantânea.
const CORES = {
  livre: '#34C759', ocupada: '#8B5CF6', aguardando: '#F59E0B',
  reservada: '#2563EB', limpeza: '#EF4444',
};
const LABEL_STATUS = {
  livre: 'Livre', ocupada: 'Ocupada', aguardando: 'Aguardando',
  reservada: 'Reservada', limpeza: 'Em Limpeza',
};

function elapsedStr(desde, agora) {
  if (!desde) return '';
  const ms = Math.max(0, agora - new Date(desde).getTime());
  const min = Math.floor(ms / 60000);
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${m} min`;
}

/**
 * F1 — Mesas/Comandas. Modal fullscreen, autocontido (busca os próprios
 * dados, mesmo padrão do ModalPagamentoVenda) — o pedido inteiro é montado
 * aqui dentro (busca/grade de produtos, item a item); só no F9 os itens da
 * comanda viram carrinho de verdade no PDV (onFinalizar).
 *
 * Status da mesa: Ocupada/Aguardando vêm da comanda vinculada; Reservada/
 * Em Limpeza são manuais (mesas.estado) — só valem quando não tem comanda
 * aberta (uma mesa ocupada nunca aparece como reservada).
 *
 * Atalhos seguem o padrão que o resto do nosso PDV já usa (não o do print
 * de referência, que é de outro sistema): F3 busca produto, F6 desconto,
 * F4 observação do item selecionado, Delete cancela item, F9 finaliza,
 * Esc fecha (ou só desseleciona a mesa, se alguma estiver selecionada).
 * Ctrl+M troca pra aba Mesa, Ctrl+C pra aba Comanda, Ctrl+B abre a busca
 * rápida por número, Ctrl+I imprime o pedido, Ctrl+A abre a comanda da
 * mesa selecionada — todos funcionam em qualquer uma das duas abas.
 */
export default function ModalMesasComandas({ aberto, abaInicial, onFechar, onFinalizar, toleranciaReservaMin, balancaAtiva, onToggleBalanca, online, nomeObrigatorio, larguraCupom, taxaEntregaHabilitada, taxaEntregaPadrao }) {
  const { usuario } = useAuth();
  const orgId = usuario?.organizacao_id;
  const tolerancia = toleranciaReservaMin || 30;
  // O componente desmonta quando `aberto` fica false (`if (!aberto) return
  // null` mais abaixo) — então este useState reinicializa do zero a cada
  // abertura, lendo `abaInicial` de novo. Usado pelo reabrir automático do
  // PDV (Parâmetros → PDV → Controle de Mesas e Comandas) pra abrir direto
  // na aba certa depois de finalizar uma mesa/comanda.
  const [tipoVenda, setTipoVenda] = useState(abaInicial || 'mesa');
  const [mesas, setMesas] = useState([]);
  const [mesaSelId, setMesaSelId] = useState(null);
  const [comanda, setComanda] = useState(null); // null = mesa livre (ou nada selecionado)
  const [itemSelId, setItemSelId] = useState(null);
  const [numPessoas, setNumPessoas] = useState('');
  const [nomeCliente, setNomeCliente] = useState('');
  const nomeClienteRef = useRef(null);
  const [desconto, setDesconto] = useState(0);

  const [produtos, setProdutos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [grupoSelId, setGrupoSelId] = useState(null);
  const [busca, setBusca] = useState('');

  const [processando, setProcessando] = useState(false);
  const abrindoRef = useRef(false);
  const [erro, setErro] = useState('');
  const [lendoBalanca, setLendoBalanca] = useState(false);
  const [agora, setAgora] = useState(Date.now()); // só pra recalcular os textos de tempo decorrido
  const [formReserva, setFormReserva] = useState(null); // {nome, telefone, dataHora, pessoas} ou null (fechado)
  const [buscaNumeroForm, setBuscaNumeroForm] = useState(null); // {valor, erro} ou null (fechado) — Ctrl+B

  const buscaRef = useRef(null);
  const mesaSel = mesas.find(m => m.id === mesaSelId) || null;
  // Mesa: 2 dígitos (01, 02...); Comanda avulsa: 3 dígitos (001, 002...050...).
  const numFmt = n => String(n).padStart(tipoVenda === 'mesa' ? 2 : 3, '0');

  const carregarMesas = useCallback((tipo) => {
    api.get('/mesas', { params: { tipo: tipo === 'individual' ? 'comanda' : 'mesa' } })
      .then(r => { setMesas(r.data || []); cacheMesas(r.data || []); })
      .catch(async (err) => {
        if (err.response) { setErro('Erro ao carregar.'); return; }
        // Sem internet: mostra o último retrato conhecido (só leitura) —
        // nunca de outra organização (mesmo dispositivo pode operar mais de
        // uma loja no mesmo navegador).
        setMesas(await getMesasCache(orgId));
      });
  }, [orgId]);

  useEffect(() => {
    if (!aberto) return;
    // abaInicial (default 'mesa') — não dá pra confiar só no useState inicial
    // de tipoVenda pra isso: este componente nunca desmonta de verdade (o
    // "if (!aberto) return null" mais abaixo só pula o render, o Fiber
    // continua vivo), então o valor inicial do useState só valeria na
    // primeiríssima abertura. Achado real (2026-09-18, vieiracoffee): o
    // reabrir automático de Comandas sempre caía na aba Mesa por causa
    // deste efeito, que ficava resetando pra 'mesa' toda vez que abria.
    const aba = abaInicial || 'mesa';
    setTipoVenda(aba);
    setMesaSelId(null);
    setComanda(null);
    setItemSelId(null);
    setNomeCliente('');
    setDesconto(0);
    setBusca('');
    setGrupoSelId(null);
    setErro('');
    setFormReserva(null);
    setBuscaNumeroForm(null);
    carregarMesas(aba);
    api.get('/grupos').then(r => setGrupos(r.data || [])).catch(() => {});
    api.get('/produtos', { params: { todos: 'true' } }).then(r => setProdutos(r.data || [])).catch(() => {});
  }, [aberto, carregarMesas, abaInicial]);

  // Troca entre a grade de MESAS (físicas) e COMANDAS (avulsas, sem mesa) —
  // reaproveita toda a estrutura/ciclo de vida já pronto de mesas, só que
  // filtrado por tipo; numeração de mesa e de comanda são independentes.
  const trocarTipoVenda = (tipo) => {
    setTipoVenda(tipo);
    setMesaSelId(null);
    setComanda(null);
    setItemSelId(null);
    setNumPessoas('');
    setNomeCliente('');
    setFormReserva(null);
    setBuscaNumeroForm(null);
    setErro('');
    carregarMesas(tipo);
  };

  // Recalcula "há X min" / relógio decorrido a cada 30s, sem precisar buscar de novo.
  useEffect(() => {
    if (!aberto) return;
    const t = setInterval(() => setAgora(Date.now()), 30000);
    return () => clearInterval(t);
  }, [aberto]);

  const carregarComandaDaMesa = useCallback((mesaId) => {
    api.get(`/comandas/${mesaId}`)
      .then(r => { setComanda(r.data); cacheComanda(mesaId, r.data); })
      .catch(async (err) => {
        if (err.response) { setComanda(null); return; }
        // Sem internet: mostra o último retrato conhecido (só leitura) —
        // itens/lançamento continuam bloqueados (ver adicionarProduto etc).
        // Nunca de outra organização (mesmo dispositivo pode operar mais de
        // uma loja no mesmo navegador).
        setComanda(await getComandaCache(mesaId, orgId));
      });
  }, [orgId]);

  const selecionarMesa = (mesa) => {
    setMesaSelId(mesa.id);
    setItemSelId(null);
    setErro('');
    setFormReserva(null);
    if (mesa.comanda_status) {
      setNumPessoas(mesa.num_pessoas ? String(mesa.num_pessoas) : '');
      carregarComandaDaMesa(mesa.id);
    } else {
      setNumPessoas('');
      setNomeCliente('');
      setComanda(null);
    }
  };

  const abrirComanda = async () => {
    if (!mesaSel || abrindoRef.current) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    const nome = nomeCliente.trim() || (statusMesa(mesaSel) === 'reservada' ? mesaSel.reserva_nome || '' : '');
    if (nomeObrigatorio && !nome) {
      setErro('Informe o nome do cliente para abrir.');
      nomeClienteRef.current?.focus();
      return;
    }
    // Ref (não estado): o estado só muda no próximo render, e um clique duplo
    // ou Enter repetido passava duas vezes por aqui antes disso.
    abrindoRef.current = true;
    setProcessando(true); setErro('');
    try {
      await api.post('/comandas', {
        mesa_id: mesaSel.id,
        num_pessoas: numPessoas ? parseInt(numPessoas) : null,
        cliente_nome: nome || null,
      });
      carregarMesas(tipoVenda);
      carregarComandaDaMesa(mesaSel.id);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao abrir a comanda.');
    } finally {
      setProcessando(false);
      // Segura mais um instante: até a comanda recarregar, a mesa ainda
      // aparece livre e um novo Enter abriria outra.
      setTimeout(() => { abrindoRef.current = false; }, 1500);
    }
  };

  const adicionarProduto = async (produto) => {
    if (!comanda || lendoBalanca) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    setErro('');
    try {
      let quantidade = 1;
      if (produto.pesado_no_caixa) {
        if (balancaAtiva) {
          setLendoBalanca(true);
          try {
            quantidade = await lerPesoBalanca();
          } finally {
            setLendoBalanca(false);
          }
        } else {
          // Balança desligada: pede o peso digitado na mão.
          const texto = window.prompt(`Peso de ${produto.nome} (kg):`, '');
          if (texto === null) return;
          const peso = parseFloat(String(texto).replace(',', '.'));
          if (!peso || peso <= 0) { setErro('Peso inválido.'); return; }
          quantidade = peso;
        }
      }
      await api.post(`/comandas/${comanda.id}/itens`, { produto_id: produto.id, quantidade });
      carregarComandaDaMesa(mesaSel.id);
      carregarMesas(tipoVenda);
    } catch (err) {
      setErro(err.response?.data?.erro || err.message || 'Erro ao adicionar o produto.');
    }
  };

  // Enter no campo de busca com um código/barcode digitado (numérico) —
  // reconhece o produto direto por código exato e adiciona, sem precisar
  // clicar no card na grade (pensado pra bipagem de balcão/leitor).
  const confirmarBuscaExata = () => {
    const termo = busca.trim();
    if (!termo || !/^\d+$/.test(termo)) return;
    // Produto pode ter mais de 1 código de barras (codigos_barras_produto) —
    // compara também com os secundários, não só o principal.
    const match = produtos.find(p => String(p.codigo_interno) === termo || p.codigo_barras === termo
      || (Array.isArray(p.codigos_barras) && p.codigos_barras.some(cb => cb.codigo === termo)));
    if (match) {
      adicionarProduto(match);
      setBusca('');
    }
  };

  const cancelarItemSelecionado = async (senha_admin) => {
    if (!comanda || !itemSelId) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    try {
      await api.delete(`/comandas/${comanda.id}/itens/${itemSelId}`, senha_admin ? { data: { senha_admin } } : undefined);
      setItemSelId(null);
      setErro('');
      carregarComandaDaMesa(mesaSel.id);
      carregarMesas(tipoVenda);
    } catch (err) {
      if (err.response?.data?.exige_senha_admin) {
        const item = comanda.itens?.find(i => i.id === itemSelId);
        const senha = await pedirSenhaAdmin(err.response.data.erro, {
          acao: 'excluir_item_comanda_apos_prazo',
          detalhes: item?.descricao || `item ${itemSelId}`,
        });
        if (senha === null) return;
        return cancelarItemSelecionado(senha);
      }
      setErro(err.response?.data?.erro || 'Erro ao cancelar o item.');
    }
  };

  const observarItemSelecionado = async () => {
    if (!comanda || !itemSelId) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    const item = comanda.itens?.find(i => i.id === itemSelId);
    const texto = window.prompt('Observação do item:', item?.observacao || '');
    if (texto === null) return;
    try {
      await api.patch(`/comandas/${comanda.id}/itens/${itemSelId}`, { observacao: texto });
      carregarComandaDaMesa(mesaSel.id);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar a observação.');
    }
  };

  // Taxa de entrega (Parâmetros → PDV) — gravada na comanda, vai pra venda no F9.
  const definirTaxaEntrega = async () => {
    if (!comanda) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    const atual = parseFloat(comanda.taxa_entrega) || 0;
    const sugestao = atual > 0 ? atual : (taxaEntregaPadrao || 0);
    const texto = window.prompt('Taxa de entrega (R$) — deixe em branco ou 0 para tirar:', sugestao > 0 ? fmtBRL(sugestao) : '');
    if (texto === null) return;
    const valor = parseFloat(String(texto).replace(/\./g, '').replace(',', '.')) || 0;
    if (valor < 0) { setErro('Taxa de entrega não pode ser negativa.'); return; }
    try {
      await api.patch(`/comandas/${comanda.id}/taxa-entrega`, { taxa_entrega: valor });
      carregarComandaDaMesa(mesaSel.id);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar a taxa de entrega.');
    }
  };

  // Observação do pedido inteiro — sai no fim da impressão, abaixo dos itens.
  const observarPedido = async () => {
    if (!comanda) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    const texto = window.prompt('Observação do pedido (sai no final da impressão):', comanda.observacao || '');
    if (texto === null) return;
    try {
      await api.patch(`/comandas/${comanda.id}/observacao`, { observacao: texto });
      carregarComandaDaMesa(mesaSel.id);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar a observação.');
    }
  };

  const abrirDesconto = () => {
    const texto = window.prompt('Desconto (R$):', String(desconto || ''));
    if (texto === null) return;
    const v = parseFloat(String(texto).replace(',', '.'));
    setDesconto(isNaN(v) ? 0 : Math.max(0, v));
  };

  const solicitarFechamento = async () => {
    if (!comanda) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    try {
      await api.patch(`/comandas/${comanda.id}/solicitar-fechamento`);
      carregarMesas(tipoVenda);
      carregarComandaDaMesa(mesaSel.id);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao solicitar fechamento.');
    }
  };

  const finalizar = () => {
    if (!comanda || !comanda.itens?.length) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    // tipo_venda vem do estado da ABA aberta agora ('mesa'/'individual'),
    // não dá pra deduzir isso depois olhando só a comanda: comanda.mesa_id
    // vem preenchido nos dois casos (comanda avulsa também é uma linha na
    // tabela mesas, só com tipo='comanda' — migration 107_mesas_tipo_
    // comanda.sql), então mesa_id sozinho não diferencia mesa de comanda
    // avulsa (achado real: PDV.jsx usava isso errado pro reabrir automático
    // de Mesas/Comandas, sempre caía em "mesa").
    onFinalizar({ ...comanda, desconto, tipo_venda: tipoVenda });
  };

  const cancelarComanda = async (senha_admin, motivo) => {
    if (!comanda) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    const rotuloCancelar = tipoVenda === 'mesa' ? 'Mesa' : 'Comanda';
    if (!senha_admin && !window.confirm(`Cancelar a ${rotuloCancelar} ${numFmt(mesaSel.numero)}? A ${rotuloCancelar.toLowerCase()} volta a ficar Livre e o que foi lançado é perdido.`)) return;
    try {
      await api.post(`/comandas/${comanda.id}/cancelar`, senha_admin ? { senha_admin, motivo } : undefined);
      setComanda(null);
      setItemSelId(null);
      setErro('');
      carregarMesas(tipoVenda);
    } catch (err) {
      if (err.response?.data?.exige_senha_admin) {
        // Motivo é pedido junto da senha (pedido explícito) — comanda vazia
        // cancela sem nenhum dos dois, só quem tem item lançado exige.
        const motivoDigitado = window.prompt(`${rotuloCancelar} tem item(ns) lançado(s) — motivo do cancelamento (mínimo 5 caracteres):`);
        if (motivoDigitado === null) return;
        if (motivoDigitado.trim().length < 5) { setErro('Motivo deve ter no mínimo 5 caracteres.'); return; }
        const senha = await pedirSenhaAdmin(err.response.data.erro, {
          acao: 'cancelar_mesa_com_itens',
          detalhes: `${rotuloCancelar} ${numFmt(mesaSel.numero)} — Motivo: ${motivoDigitado.trim()}`,
        });
        if (senha === null) return;
        return cancelarComanda(senha, motivoDigitado.trim());
      }
      setErro(err.response?.data?.erro || 'Erro ao cancelar a mesa.');
    }
  };

  // ── Reservar / Em Limpeza (estado manual da mesa) ─────────────
  const atualizarEstadoMesa = async (payload) => {
    if (!mesaSel) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    setErro('');
    try {
      await api.patch(`/mesas/${mesaSel.id}/estado`, payload);
      carregarMesas(tipoVenda);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao atualizar a mesa.');
    }
  };

  const abrirFormReserva = () => setFormReserva({ nome: '', telefone: '', dataHora: '', pessoas: '' });
  const fecharFormReserva = () => setFormReserva(null);
  const confirmarReserva = async () => {
    if (!formReserva?.nome || !formReserva?.dataHora) {
      setErro('Informe ao menos o nome e a data/hora da reserva.');
      return;
    }
    await atualizarEstadoMesa({
      estado: 'reservada',
      reserva_nome: formReserva.nome,
      reserva_telefone: formReserva.telefone || null,
      reserva_data_hora: new Date(formReserva.dataHora).toISOString(),
      reserva_pessoas: parseInt(formReserva.pessoas) || null,
    });
    setFormReserva(null);
  };
  const cancelarReserva = () => atualizarEstadoMesa({ estado: 'disponivel' });
  const marcarLimpeza = () => atualizarEstadoMesa({ estado: 'limpeza' });
  const concluirLimpeza = () => atualizarEstadoMesa({ estado: 'disponivel' });

  // ── Localizar mesa/comanda por número (Ctrl+B) ────────────────
  const abrirBuscaNumero = () => setBuscaNumeroForm({ valor: '', erro: '' });
  const confirmarBuscaNumero = () => {
    const rotuloLocal = tipoVenda === 'mesa' ? 'Mesa' : 'Comanda';
    const num = parseInt(buscaNumeroForm?.valor, 10);
    if (!num) { setBuscaNumeroForm(f => ({ ...f, erro: 'Informe um número.' })); return; }
    const m = mesas.find(x => x.numero === num);
    if (!m) { setBuscaNumeroForm(f => ({ ...f, erro: `${rotuloLocal} ${num} não encontrada.` })); return; }
    selecionarMesa(m);
    setBuscaNumeroForm(null);
  };

  // ── Imprimir pedido (conferência, sem valor fiscal) ───────────
  const imprimirPedido = async () => {
    if (!comanda || !mesaSel) return;
    const rotuloLocal = tipoVenda === 'mesa' ? 'Mesa' : 'Comanda';
    const numFmtLocal = numFmt(mesaSel.numero);
    const clienteNome = comanda.cliente_nome || nomeCliente;
    const itensImp = comanda.itens || [];
    const subtotalImp = itensImp.reduce((acc, i) => acc + parseFloat(i.valor_total), 0);
    const taxaImp = parseFloat(comanda.taxa_entrega) || 0;
    const itensHtml = itensImp.map(it => `
      <div class="it">
        <p>${parseFloat(it.quantidade)}x ${it.descricao}</p>
        <p class="row"><span>R$ ${fmtBRL(it.valor_unitario)} un.</span><span>R$ ${fmtBRL(it.valor_total)}</span></p>
        ${it.observacao ? `<p style="font-size:12px;">Obs: ${it.observacao}</p>` : ''}
      </div>`).join('');
    // Itens no mesmo tamanho do título (14px) pra cozinha ler de longe;
    // 5mm à esquerda: a térmica do lanchedajack comia ~2 caracteres na borda.
    // largura respeita a margem de segurança do PDV (impressaoMargem.js).
    const larguraCSS = larguraEfetivaCSS(larguraCupom || '80mm');
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
<title>${rotuloLocal} ${numFmtLocal}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Courier New',Courier,monospace; font-size:11px; font-weight:600; width:${larguraCSS}; padding:6px 6px 6px 5mm; }
  .c { text-align:center; } .b { font-weight:bold; }
  .hr { border:none; border-top:1px dashed #000; margin:5px 0; }
  .row { display:flex; justify-content:space-between; }
  .it { margin:4px 0; font-size:14px; font-weight:bold; } .grand { font-size:13px; }
  @media print { @page { margin:0; size:${larguraCSS} auto; } }
</style></head><body>
  <p class="c b" style="font-size:14px;">${rotuloLocal.toUpperCase()} ${numFmtLocal}</p>
  <p class="c">${new Date().toLocaleString('pt-BR')}</p>
  ${clienteNome ? `<p class="c">Cliente: ${clienteNome}</p>` : ''}
  <hr class="hr">
  ${itensHtml || '<p class="c">Nenhum item lançado.</p>'}
  ${comanda.observacao ? `<hr class="hr"><p style="font-size:13px;font-weight:bold;">OBS. DO PEDIDO: ${comanda.observacao}</p>` : ''}
  <hr class="hr">
  <p class="row b"><span>Subtotal</span><span>R$ ${fmtBRL(subtotalImp)}</span></p>
  ${desconto > 0 ? `<p class="row"><span>Desconto</span><span>- R$ ${fmtBRL(desconto)}</span></p>` : ''}
  ${taxaImp > 0 ? `<p class="row"><span>Taxa de entrega</span><span>+ R$ ${fmtBRL(taxaImp)}</span></p>` : ''}
  <p class="row grand b"><span>TOTAL</span><span>R$ ${fmtBRL(Math.max(0, subtotalImp - desconto) + taxaImp)}</span></p>
  <hr class="hr">
  <p class="c" style="font-size:9px;">Documento sem valor fiscal — conferência do pedido</p>
</body></html>`;
    if (await tentarImprimirDiretoHtml(html)) return;
    const win = window.open('', '_blank', 'width=400,height=700');
    if (!win) { setErro('Permita pop-ups para imprimir.'); return; }
    win.document.write(html);
    win.document.close();
    fecharComEsc(win);
    win.focus();
    win.print();
  };

  // ── Atalhos do modal (próprios, enquanto estiver aberto) ──────
  useEffect(() => {
    if (!aberto) return;
    const handler = (e) => {
      if (buscaNumeroForm) {
        if (e.key === 'Escape') { e.preventDefault(); setBuscaNumeroForm(null); }
        if (e.key === 'Enter') { e.preventDefault(); confirmarBuscaNumero(); }
        return;
      }
      if (formReserva) {
        if (e.key === 'Escape') { e.preventDefault(); fecharFormReserva(); }
        return;
      }
      if (e.ctrlKey && (e.key === 'm' || e.key === 'M')) { e.preventDefault(); trocarTipoVenda('mesa'); return; }
      if (e.ctrlKey && (e.key === 'c' || e.key === 'C')) {
        if (document.activeElement?.tagName === 'INPUT') return; // preserva Ctrl+C de copiar texto em campos
        e.preventDefault(); trocarTipoVenda('individual'); return;
      }
      if (e.ctrlKey && (e.key === 'b' || e.key === 'B')) { e.preventDefault(); abrirBuscaNumero(); return; }
      if (e.ctrlKey && (e.key === 'i' || e.key === 'I')) {
        if (document.activeElement?.tagName === 'INPUT') return;
        e.preventDefault(); imprimirPedido(); return;
      }
      if (e.ctrlKey && (e.key === 'a' || e.key === 'A')) {
        if (document.activeElement?.tagName === 'INPUT') return; // preserva Ctrl+A de selecionar tudo em campos
        e.preventDefault();
        const jaAberta = comanda && ['aberta', 'aguardando_fechamento'].includes(comanda.status);
        if (mesaSel && !jaAberta && !processando) abrirComanda();
        return;
      }
      if (document.activeElement === buscaRef.current && e.key !== 'Escape' && e.key !== 'F3') return;
      if (e.key === 'F3') { e.preventDefault(); buscaRef.current?.focus(); return; }
      if (e.key === 'F6') { e.preventDefault(); abrirDesconto(); return; }
      if (e.key === 'F4') { e.preventDefault(); observarItemSelecionado(); return; }
      if (e.key === 'Delete') { e.preventDefault(); cancelarItemSelecionado(); return; }
      if (e.key === 'F9') { e.preventDefault(); finalizar(); return; }
      if (e.key === 'Escape') {
        e.preventDefault();
        if (mesaSelId) { setMesaSelId(null); setComanda(null); } else { onFechar(); }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [aberto, mesaSelId, comanda, itemSelId, desconto, formReserva, buscaNumeroForm, mesas, tipoVenda, processando]);

  if (!aberto) return null;

  const statusMesa = m => m.comanda_status === 'aguardando_fechamento' ? 'aguardando'
    : m.comanda_status === 'aberta' ? 'ocupada'
    : m.estado === 'reservada' ? 'reservada'
    : m.estado === 'limpeza' ? 'limpeza'
    : 'livre';

  // Mais recente no topo (o último pedido lançado é o que o caixa procura).
  const ocupadas = mesas.filter(m => ['ocupada', 'aguardando'].includes(statusMesa(m)))
    .sort((a, b) => new Date(b.aberta_em || 0) - new Date(a.aberta_em || 0));
  const minutosEspera = m => m.aberta_em ? Math.floor((agora - new Date(m.aberta_em).getTime()) / 60000) : 0;
  const contagem = mesas.reduce((acc, m) => {
    const st = statusMesa(m);
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});

  const produtosFiltrados = produtos.filter(p => {
    if (grupoSelId && p.grupo_id !== grupoSelId) return false;
    if (busca && !p.nome.toLowerCase().includes(busca.toLowerCase())
        && !String(p.codigo_interno).includes(busca)
        && !(p.codigo_barras || '').includes(busca)
        && !(Array.isArray(p.codigos_barras) && p.codigos_barras.some(cb => (cb.codigo || '').includes(busca)))) return false;
    return true;
  });

  const itensAtivos = comanda?.itens || [];
  const subtotal = itensAtivos.reduce((s2, i) => s2 + parseFloat(i.valor_total), 0);
  const taxaEntrega = parseFloat(comanda?.taxa_entrega) || 0;
  const total = Math.max(0, subtotal - desconto) + taxaEntrega;
  const comandaAberta = comanda && ['aberta', 'aguardando_fechamento'].includes(comanda.status);
  const statusSel = mesaSel ? statusMesa(mesaSel) : null;
  const aguardandoMuito = comanda?.status === 'aguardando_fechamento' && comanda.aguardando_desde
    && (agora - new Date(comanda.aguardando_desde).getTime()) > 5 * 60000;

  const horaChegou = m => m.estado === 'reservada' && m.reserva_data_hora && agora >= new Date(m.reserva_data_hora).getTime();
  const minutosAtraso = m => m.reserva_data_hora ? Math.floor((agora - new Date(m.reserva_data_hora).getTime()) / 60000) : 0;
  const atrasada = m => horaChegou(m) && minutosAtraso(m) > tolerancia;

  // Comanda avulsa (aba COMANDA) reaproveita a mesma grade/ciclo de vida
  // das mesas — só muda o rótulo na tela e alguns recursos exclusivos de
  // mesa física (reserva com horário, "em limpeza") ficam escondidos.
  const ehMesa = tipoVenda === 'mesa';
  const rotulo = ehMesa ? 'Mesa' : 'Comanda';
  const rotuloPlural = ehMesa ? 'MESAS' : 'COMANDAS';
  const statusesResumo = ehMesa
    ? ['livre', 'ocupada', 'reservada', 'aguardando', 'limpeza']
    : ['livre', 'ocupada', 'aguardando'];

  return (
    <div className={s.tela}>
      {/* ── Esquerda: mesas/comandas ── */}
      <div className={s.esquerda}>
        <div className={s.tipoVendaRow}>
          <button type="button" className={`${s.tipoVendaBtn} ${s.tipoVendaBtnMesa} ${tipoVenda === 'mesa' ? s.tipoVendaBtnAtivo : ''}`}
            onClick={() => trocarTipoVenda('mesa')}>
            <span>🍽️ MESA</span>
            <span className={s.tipoVendaAtalho}>Ctrl+M</span>
          </button>
          <button type="button" className={`${s.tipoVendaBtn} ${s.tipoVendaBtnIndividual} ${tipoVenda === 'individual' ? s.tipoVendaBtnAtivo : ''}`}
            onClick={() => trocarTipoVenda('individual')}>
            <span>🧾 COMANDA</span>
            <span className={s.tipoVendaAtalho}>Ctrl+C</span>
          </button>
        </div>

        <>
            <div className={s.resumoBox}>
              {statusesResumo.map(st => (
                <div key={st} className={s.resumoLinha}>
                  <span className={s.bolinha} style={{ background: CORES[st] }} />
                  <span className={s.resumoLabel}>{st === 'aguardando' ? 'Fechando' : LABEL_STATUS[st]}</span>
                  <span className={s.resumoValor}>{contagem[st] || 0}</span>
                </div>
              ))}
            </div>

            <div className={s.mesasTitulo}>{rotuloPlural}</div>
            <div className={s.gradeWrap}>
              <div className={s.grade}>
                {mesas.map(m => {
                  const st = statusMesa(m);
                  const chegou = st === 'reservada' && horaChegou(m);
                  return (
                    <div key={m.id} className={`${s.mesaCard} ${!ehMesa ? s.mesaCardComanda : ''} ${m.id === mesaSelId ? s.mesaSelecionada : ''} ${chegou ? s.mesaReservaChegou : ''}`}
                      style={{ background: CORES[st], color: '#fff' }}
                      onClick={() => selecionarMesa(m)}>
                      <span className={s.mesaNumero}>{numFmt(m.numero)}</span>
                      {st === 'livre' && <span className={s.mesaValor}>Livre</span>}
                      {(st === 'ocupada' || st === 'aguardando') && <span className={s.mesaValor}>{`R$ ${fmtBRL(m.valor_parcial)}`}</span>}
                      {st === 'reservada' && <span className={s.mesaValor}>{m.reserva_data_hora ? fmtHora(m.reserva_data_hora) : 'Reservada'}</span>}
                      {st === 'limpeza' && <span className={s.mesaValor}>Limpeza</span>}
                    </div>
                  );
                })}
              </div>
            </div>
            {ocupadas.length > 0 && (
              <div className={s.ocupadasBox}>
                <div className={s.ocupadasTitulo}>{rotuloPlural} OCUPADAS</div>
                {ocupadas.map(m => (
                  <div key={m.id} className={s.ocupadaCard} onClick={() => selecionarMesa(m)}>
                    <span className={`${s.ocupadaNumero} ${!ehMesa ? s.ocupadaNumeroComanda : ''}`} style={{ background: CORES[statusMesa(m)] }}>
                      {numFmt(m.numero)}
                    </span>
                    <span className={s.ocupadaInfo}>
                      {m.cliente_nome && <span className={s.ocupadaNome}>{m.cliente_nome}</span>}
                      <span className={s.ocupadaHoraValor}>
                        <span className={s.ocupadaHora}>
                          {fmtHora(m.aberta_em)}
                          {m.num_pessoas ? <span className={s.ocupadaPessoas}>{` · ${m.num_pessoas} pessoa(s)`}</span> : null}
                        </span>
                        <span className={`${s.ocupadaEspera} ${minutosEspera(m) >= 40 ? s.ocupadaEsperaAlta : minutosEspera(m) >= 20 ? s.ocupadaEsperaMedia : ''}`}
                          title="Tempo de espera desde a abertura">
                          ⏱ {elapsedStr(m.aberta_em, agora)}
                        </span>
                        <span className={s.ocupadaValor}>{`R$ ${fmtBRL(m.valor_parcial)}`}</span>
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
        </>
      </div>

      {/* ── Central: busca + categorias + produtos ── */}
      <div className={s.central}>
        <div className={s.buscaBar}>
          <input ref={buscaRef} className={s.buscaInput} placeholder="Buscar produto... (F3)"
            value={busca} onChange={e => setBusca(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirmarBuscaExata(); } }} />
          {!online && (
            <span className={s.btnBalanca} style={{ cursor: 'default' }} title="Sem conexão — mesas/comandas ficam só pra consulta até a internet voltar">
              🟡 Offline — só consulta
            </span>
          )}
          <button
            type="button"
            className={`${s.btnBalanca} ${balancaAtiva ? s.btnBalancaAtiva : s.btnBalancaInativa}`}
            onClick={onToggleBalanca}
            title={balancaAtiva
              ? 'Balança ligada — clique pra desligar e informar o peso na mão'
              : 'Balança desligada — peso é digitado na mão. Clique pra voltar a capturar da balança'}
          >
            ⚖️ Balança: {balancaAtiva ? 'Ligada' : 'Desligada'}
          </button>
          <button type="button" className={s.btnFechar} onClick={onFechar}>✕ Fechar (Esc)</button>
        </div>
        <div className={s.categorias}>
          <button type="button" className={`${s.categoriaBtn} ${!grupoSelId ? s.categoriaBtnAtiva : ''}`}
            onClick={() => setGrupoSelId(null)}>Todos</button>
          {grupos.map(g => (
            <button key={g.id} type="button" className={`${s.categoriaBtn} ${grupoSelId === g.id ? s.categoriaBtnAtiva : ''}`}
              onClick={() => setGrupoSelId(g.id)}>{g.nome}</button>
          ))}
        </div>
        <div className={s.produtosWrap}>
          {!comandaAberta ? (
            <div className={s.produtosVazio}>
              {mesaSel
                ? (ehMesa ? 'Abra a comanda desta mesa (à direita) pra começar a lançar produtos.' : 'Abra esta comanda (à direita) pra começar a lançar produtos.')
                : `Selecione uma ${rotulo.toLowerCase()} ao lado.`}
            </div>
          ) : produtosFiltrados.length === 0 ? (
            <div className={s.produtosVazio}>Nenhum produto encontrado.</div>
          ) : (
            <div className={s.produtosGrade}>
              {produtosFiltrados.map(p => (
                <div key={p.id} className={s.produtoCard} onClick={() => adicionarProduto(p)}>
                  {p.foto_url ? (
                    <img className={s.produtoFoto} src={p.foto_url} alt={p.nome} />
                  ) : (
                    <span className={s.produtoFotoVazia}>📦</span>
                  )}
                  <span className={s.produtoNome}>{p.nome}</span>
                  <span className={s.produtoPreco}>{fmtBRL(p.preco_venda)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Direita: comanda da mesa selecionada ── */}
      <div className={s.direita}>
        {lendoBalanca && <div className={s.avisoBar}>⚖️ Lendo peso da balança…</div>}
        {erro && <div className={s.erroBar}>{erro}</div>}
        {!mesaSel ? (
          <div className={s.direitaVazio}>{`Selecione uma ${rotulo.toLowerCase()} para ver ou abrir${ehMesa ? ' a comanda' : ''}.`}</div>
        ) : (
          <>
            <div className={s.direitaHeader}>
              <div className={s.direitaTitulo}>
                {ehMesa ? '🍽️' : '🧾'} {rotulo} {numFmt(mesaSel.numero)}
                {comandaAberta && comanda?.cliente_nome && <span className={s.direitaCliente}> — {comanda.cliente_nome}</span>}
              </div>
              {statusSel === 'ocupada' && (
                <div className={s.direitaPessoas}>🕒 {elapsedStr(comanda?.aberta_em, agora)} · {fmtBRL(mesaSel.valor_parcial)}</div>
              )}
              {statusSel === 'aguardando' && (
                <div className={s.direitaPessoas} style={aguardandoMuito ? { color: '#dc2626', fontWeight: 700 } : undefined}>
                  🔔 Solicitou conta · há {elapsedStr(comanda?.aguardando_desde, agora)}
                </div>
              )}
              {statusSel === 'reservada' && (
                <div className={s.direitaPessoas} style={horaChegou(mesaSel) ? { color: '#6d28d9', fontWeight: 700 } : undefined}>
                  🔵 Reservada · {fmtHora(mesaSel.reserva_data_hora)} · {mesaSel.reserva_nome} · {mesaSel.reserva_pessoas} pessoa(s)
                  {mesaSel.reserva_telefone ? ` · ${mesaSel.reserva_telefone}` : ''}
                </div>
              )}
              {statusSel === 'limpeza' && (
                <div className={s.direitaPessoas}>🔴 Em Limpeza</div>
              )}
              {statusSel === 'livre' && !comandaAberta && (
                <div className={s.direitaPessoas}>
                  👤 <input type="number" min="1" value={numPessoas}
                    onChange={e => setNumPessoas(e.target.value)} /> pessoa(s)
                  <input type="text" ref={nomeClienteRef}
                    className={`${s.inputNomeCliente} ${nomeObrigatorio && !nomeCliente.trim() ? s.inputObrigatorio : ''}`}
                    placeholder={nomeObrigatorio ? 'Nome do cliente (obrigatório)' : 'Nome do cliente (opcional)'}
                    value={nomeCliente} onChange={e => setNomeCliente(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); abrirComanda(); } }} />
                </div>
              )}
            </div>

            {statusSel === 'reservada' && atrasada(mesaSel) && (
              <div className={s.erroBar} style={{ margin: '0 16px', marginTop: 10 }}>
                Cliente não compareceu há {minutosAtraso(mesaSel)} min.
                <button type="button" className={s.btnCancelarReserva} style={{ marginLeft: 8 }} onClick={cancelarReserva}>Liberar mesa</button>
              </div>
            )}

            {!comandaAberta ? (
              <div className={s.botoesFooter} style={{ marginTop: 'auto' }}>
                {statusSel === 'reservada' ? (
                  <>
                    <button type="button" className={s.btnAbrir} disabled={processando} onClick={abrirComanda}>
                      {processando ? 'Abrindo…' : 'INICIAR ATENDIMENTO'}
                    </button>
                    <button type="button" className={s.btnCancelarReserva} onClick={cancelarReserva}>Cancelar Reserva</button>
                  </>
                ) : statusSel === 'limpeza' ? (
                  <button type="button" className={s.btnAbrir} onClick={concluirLimpeza}>✅ Concluir Limpeza</button>
                ) : (
                  <>
                    <button type="button" className={s.btnAbrir} disabled={processando} onClick={abrirComanda}>
                      {processando ? 'Abrindo…' : `${ehMesa ? 'ABRIR MESA' : 'ABRIR COMANDA'} (Ctrl+A)`}
                    </button>
                    {ehMesa && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className={s.btnReservar} onClick={abrirFormReserva}>🔵 Reservar</button>
                        <button type="button" className={s.btnLimpeza} onClick={marcarLimpeza}>🔴 Em Limpeza</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <>
                <div className={s.itensLista}>
                  {itensAtivos.length === 0 ? (
                    <div className={s.itensVazio}>Nenhum item lançado ainda — clique num produto ao lado.</div>
                  ) : itensAtivos.map(it => (
                    <div key={it.id} className={`${s.itemLinha} ${it.id === itemSelId ? s.itemLinhaSel : ''}`}
                      onClick={() => setItemSelId(it.id)}>
                      <span className={s.itemInfo}>
                        <span className={s.itemDescricao}>{parseFloat(it.quantidade)}x {it.descricao}</span>
                        {it.observacao && <span className={s.itemObs}>{it.observacao}</span>}
                      </span>
                      <span className={s.itemValor}>{fmtBRL(it.valor_total)}</span>
                      <button type="button" className={s.itemDelete} title="Remover item (Delete)"
                        onClick={(e) => { e.stopPropagation(); setItemSelId(it.id); cancelarItemSelecionado(); }}>🗑️</button>
                    </div>
                  ))}
                </div>

                <button type="button" className={s.obsBtn} onClick={observarItemSelecionado} disabled={!itemSelId}>
                  + Observação no item (F4)
                </button>
                {taxaEntregaHabilitada && (
                  <button type="button" className={s.obsBtn} onClick={definirTaxaEntrega} disabled={!comanda}>
                    🛵 {taxaEntrega > 0 ? `Taxa de entrega: ${fmtBRL(taxaEntrega)}` : '+ Taxa de entrega'}
                  </button>
                )}
                <button type="button" className={s.obsBtn} onClick={observarPedido} disabled={!comanda}>
                  + Observação do pedido
                </button>
                {comanda?.observacao && <div className={s.obsPedido}>📝 {comanda.observacao}</div>}

                <div className={s.totaisBox}>
                  <div className={s.totaisLinha}><span>Subtotal</span><span>{fmtBRL(subtotal)}</span></div>
                  <div className={`${s.totaisLinha} ${s.totaisDesconto}`}><span>Desconto (F6)</span><span>{fmtBRL(desconto)}</span></div>
                  {(taxaEntregaHabilitada || taxaEntrega > 0) && (
                    <div className={s.totaisLinha} style={{ cursor: 'pointer' }} onClick={definirTaxaEntrega} title="Clique para alterar a taxa de entrega">
                      <span>🛵 Taxa de entrega</span><span>{fmtBRL(taxaEntrega)}</span>
                    </div>
                  )}
                  <div className={`${s.totaisLinha} ${s.totaisTotal}`}><span>💲 Total</span><span>{fmtBRL(total)}</span></div>
                </div>

                <div className={s.botoesFooter}>
                  {comanda.status === 'aberta' && (
                    <button type="button" className={s.btnSolicitar} onClick={solicitarFechamento}>
                      🔔 Solicitar Fechamento
                    </button>
                  )}
                  <button type="button" className={s.btnFinalizar} disabled={!itensAtivos.length} onClick={finalizar}>
                    ✓ FINALIZAR VENDA (F9)
                  </button>
                  <button type="button" className={s.btnImprimir} disabled={!itensAtivos.length} onClick={imprimirPedido}>
                    🖨️ Imprimir Pedido (Ctrl+I)
                  </button>
                  <button type="button" className={s.btnCancelarMesa} onClick={() => cancelarComanda()}>
                    {`✕ Cancelar ${rotulo}`}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {formReserva && (
        <div className={s.reservaOverlay}>
          <div className={s.reservaBox}>
            <div className={s.reservaTitulo}>🔵 Reservar Mesa {numFmt(mesaSel?.numero)}</div>
            <label className={s.reservaCampo}>
              Nome do cliente
              <input autoFocus value={formReserva.nome}
                onChange={e => setFormReserva({ ...formReserva, nome: e.target.value })} />
            </label>
            <label className={s.reservaCampo}>
              Telefone (opcional)
              <input value={formReserva.telefone}
                onChange={e => setFormReserva({ ...formReserva, telefone: e.target.value })} />
            </label>
            <label className={s.reservaCampo}>
              Data e horário
              <input type="datetime-local" value={formReserva.dataHora}
                onChange={e => setFormReserva({ ...formReserva, dataHora: e.target.value })} />
            </label>
            <label className={s.reservaCampo}>
              Quantidade de pessoas
              <input type="number" min="1" value={formReserva.pessoas}
                onChange={e => setFormReserva({ ...formReserva, pessoas: e.target.value })} />
            </label>
            {erro && <div className={s.erroBar}>{erro}</div>}
            <div className={s.reservaBotoes}>
              <button type="button" className={s.btnAbrir} onClick={confirmarReserva}>Confirmar Reserva</button>
              <button type="button" className={s.btnCancelarReserva} onClick={fecharFormReserva}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {buscaNumeroForm && (
        <div className={s.reservaOverlay}>
          <div className={s.reservaBox}>
            <div className={s.reservaTitulo}>🔎 Localizar {rotulo}</div>
            <label className={s.reservaCampo}>
              {`Número da ${rotulo.toLowerCase()}`}
              <input autoFocus type="number" min="1" value={buscaNumeroForm.valor}
                onChange={e => setBuscaNumeroForm({ valor: e.target.value, erro: '' })} />
            </label>
            {buscaNumeroForm.erro && <div className={s.erroBar}>{buscaNumeroForm.erro}</div>}
            <div className={s.reservaBotoes}>
              <button type="button" className={s.btnAbrir} onClick={confirmarBuscaNumero}>Localizar (Enter)</button>
              <button type="button" className={s.btnCancelarReserva} onClick={() => setBuscaNumeroForm(null)}>Cancelar (Esc)</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
